import { useClipboard, useColorModeValue, useToast } from '@chakra-ui/react';
import { usePrevious } from '@gamma/hooks';
import { PERFORMANCE_TIMEOUT } from '@gamma/theme';
import { debounce } from 'lodash';
import Monaco from 'monaco-editor';
import { forwardRef, ReactNode, useEffect, useRef, useState } from 'react';
import {
  EditorActionsTab,
  IMonacoEditor,
  IRefEditorInstance,
  MonacoEditor,
  MonacoEditorProps,
} from '../../Components';
import { EditorHintsWrapper } from '../../Components/EditorHintsWrapper/EditorHintsWrapper';
import { OceanicNext, SlushAndPoppies } from '../../themes';

export interface CodeEditorProps extends Omit<MonacoEditorProps, 'theme'> {
  isFullScreen?: boolean;
  isFullScreenOnOpen?: () => void;
  isCopyable?: boolean;
  copyTooltip?: string;
  toastOnCopy?: boolean;
  'data-testid'?: string;
  value?: string;
  actions?: ReactNode;
  hints?: ReactNode;
}

// this is so stupid but the type from monaco-editor isn't compatible with @uiw/react-monacoeditor
type EditorType = Parameters<Required<MonacoEditorProps>['editorDidMount']>[0];

export const CodeEditor = forwardRef<IRefEditorInstance, CodeEditorProps>(
  (
    {
      options,
      value: propsValue,
      isFullScreen = false,
      isFullScreenOnOpen,
      isCopyable = true,
      toastOnCopy = true,
      copyTooltip: _copyTooltip,
      editorDidMount,
      onChange,
      'data-testid': dataTestId = 'code-editor',
      actions,
      defaultValue,
      height,
      hints,
      ...rest
    }: CodeEditorProps,
    ref,
  ) => {
    const editorRef = useRef<EditorType>();
    const monacoRef = useRef<IMonacoEditor>();
    const [value, setValue] = useState<string>(defaultValue ?? '');
    const theme = useColorModeValue('SlushAndPoppies', 'vs-dark');
    const oldPropsValue = usePrevious(propsValue);
    const { hasCopied } = useClipboard(value);
    const toast = useToast();

    useEffect(() => {
      if (toastOnCopy && hasCopied) {
        toast({
          position: 'top',
          status: 'success',
          description: `Copied to clipboard.`,
        });
      }
    }, [hasCopied, toast, toastOnCopy]);

    useEffect(() => {
      // debounce with a tiny timeout to allow editor to accurately measure its parent
      //* keeps editor from overflowing due to 100% height
      const handleResize = debounce(() => {
        editorRef.current?.layout();
      }, PERFORMANCE_TIMEOUT);

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    // update local state value whenever it is updated via value prop
    useEffect(() => {
      if (propsValue !== undefined && propsValue !== oldPropsValue) {
        setValue(propsValue);
      }
    }, [propsValue, oldPropsValue]);

    // update the editor content whenever the local state value is changed
    useEffect(() => {
      if (value !== editorRef.current?.getValue()) {
        editorRef.current?.setValue(value);
      }
    }, [value]);

    const handleEditorMount: Required<MonacoEditorProps>['editorDidMount'] = (
      editor,
      monaco,
    ) => {
      editorRef.current = editor;
      monacoRef.current = monaco;

      monacoRef.current.editor.defineTheme(
        'OceanicNext',
        OceanicNext as Monaco.editor.IStandaloneThemeData,
      );
      monacoRef.current.editor.defineTheme(
        'SlushAndPoppies',
        SlushAndPoppies as Monaco.editor.IStandaloneThemeData,
      );

      editorDidMount?.(editor, monaco);
    };

    const handleEditorChange: Required<MonacoEditorProps>['onChange'] = (
      value,
      event,
    ) => {
      setValue(value);
      onChange?.(value, event);
    };

    useEffect(() => {
      editorRef.current?.layout();
    }, [height]);

    return (
      <MonacoEditor
        onChange={handleEditorChange}
        editorDidMount={handleEditorMount}
        theme={theme}
        style={{
          position: 'relative',
        }}
        options={{
          wordWrap: 'bounded',
          wordWrapColumn: 80,
          wrappingIndent: 'indent',
          rulers: [80],
          tabSize: 2,
          padding: {
            top: 12,
            bottom: 12,
          },
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          ...options,
        }}
        ref={ref}
        height={height}
        {...rest}
        data-testid={dataTestId}
      >
        <EditorHintsWrapper data-testid={`${dataTestId}-hints`}>
          {hints}
        </EditorHintsWrapper>

        <EditorActionsTab
          value={value}
          isCopyable={isCopyable}
          isFullScreen={isFullScreen}
          isFullScreenOnOpen={isFullScreenOnOpen}
          toastOnCopy={toastOnCopy}
          data-testid={`${dataTestId}-action-tab`}
        >
          {actions}
        </EditorActionsTab>
      </MonacoEditor>
    );
  },
);
